import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { lightColors, StyledButton } from '@tryhackme/thm-ui-components';
import type { FC } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SupportedLanguage, useIsFrench, useLanguage } from 'src/common/contexts/language-context';
import { useGetUserQuery } from 'src/common/slices';
import { useGetInfluencersCampaignsQuery } from 'src/features/admin/campaigns/campaigns.slice';
import { useCampaignCta } from 'src/features/campaigns/hooks/use-campaign-cta';
import { ThemeProvider, useTheme } from 'styled-components';
import { useInfluencerConfigs } from '../../hooks/use-influencer-additional-configs';
import { useInfluencerSubscriptionCampaign } from '../../hooks/use-influencer-campaign';
import { useShowInfluencerCampaignBar } from '../influencer-campaign-bar/hooks/use-show-influencer-campaign-bar';
import { InfluencerIcon } from '../influencer-icon';
import { StyledBar, StyledCampaignPromotionText, StyledCloseBtn, StyledCopyAndIconWrapper } from './influencer-subscription-campaign-bar.styles';
import type { InfluencerSubscriptionCampaignBarProps } from './influencer-subscription-campaign-bar.types';
export const InfluencerSubscriptionCampaignBar: FC<InfluencerSubscriptionCampaignBarProps> = ({
  influencerCampaign,
  isEmbedded,
  screenEmbedded,
  hideInfluencerDetails = false
}) => {
  const theme = useTheme();
  const {
    pathname
  } = useLocation();
  const isUserPreferenceFrench = useIsFrench();
  const {
    setLanguage,
    currentLanguage
  } = useLanguage();
  const {
    data: userDataResponse
  } = useGetUserQuery();
  const [ctaText, loggedUserUrlToNavigate] = useCampaignCta(currentLanguage);
  const {
    data: campaigns
  } = useGetInfluencersCampaignsQuery({
    page: 1,
    limit: 1,
    name: influencerCampaign
  });
  const campaignData = campaigns?.data?.docs[0];
  const campaignConfigs = useInfluencerSubscriptionCampaign(influencerCampaign, campaignData);
  const [isShowInfluencerCampaignBar, hideInfluencerCampaignBar] = useShowInfluencerCampaignBar();
  const additionalConfigs = useInfluencerConfigs(campaignData);
  if (!campaignConfigs || !additionalConfigs) return null;
  const {
    campaign,
    isInCurrentInfluencerSession,
    userFriendlyName,
    percentageOff
  } = campaignConfigs;
  const {
    barCta,
    icon,
    urlToNavigate
  } = additionalConfigs;
  const url = userDataResponse?.data ? loggedUserUrlToNavigate : urlToNavigate;
  if (!campaign || !isShowInfluencerCampaignBar) return null;
  setLanguage(campaign.isFrench ? SupportedLanguage.FRENCH : SupportedLanguage.ENGLISH);
  const campaignLanguage = isUserPreferenceFrench ? SupportedLanguage.FRENCH : SupportedLanguage.ENGLISH;
  const barCtaContent = barCta ? barCta(userFriendlyName, percentageOff, pathname === '/onboarding', campaignLanguage, hideInfluencerDetails) : null;
  const isGradient = campaign.name === 'FIRESHIP_2025';
  const canTakeActions = !['/why-subscribe', '/pricing', '/onboarding'].includes(pathname);
  return !!isInCurrentInfluencerSession && !!campaignData ? <ThemeProvider theme={{
    ...theme,
    colors: lightColors
  }}>
      <StyledBar isGradient={isGradient} isEmbedded={isEmbedded} screenEmbedded={screenEmbedded} role="region" aria-label="Influencer Special Offer">
        {icon ? <StyledCopyAndIconWrapper>
            {icon && userFriendlyName && <InfluencerIcon icon={icon} alt={userFriendlyName} hideInfluencerDetails={hideInfluencerDetails} />}
            <p data-testid="campaign-promotion-text">{barCtaContent}</p>
          </StyledCopyAndIconWrapper> : <StyledCampaignPromotionText data-testid="campaign-promotion-text">
            <span>Limited time offer:&nbsp;</span>
            {barCtaContent}
          </StyledCampaignPromotionText>}
        {canTakeActions && <>
            <StyledButton as={Link} to={url ?? ''} variant="solid" color="primary" size="small">
              {ctaText}
            </StyledButton>
            {!screenEmbedded && <StyledCloseBtn onClick={hideInfluencerCampaignBar}>
                <FontAwesomeIcon icon={faXmark} />
              </StyledCloseBtn>}
          </>}
      </StyledBar>
    </ThemeProvider> : null;
};